// src/SallerAdmin/pages/Profile/BankDetailsPage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { 
    Building2, User, Landmark, Save, Loader2, 
    CheckCircle, AlertCircle, X, CreditCard, ShieldCheck
} from 'lucide-react';

// --- TOAST NOTIFICATION ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[100] flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={18} className="text-green-600"/> : <AlertCircle size={18} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

// --- HELPER COMPONENTS (Compact) ---
const ReadOnlyField = ({ label, value, className = "" }: any) => (
    <div className={`w-full ${className}`}>
        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1 tracking-wider">{label}</label>
        <div className="w-full border border-gray-200 bg-gray-50 rounded-md py-2 px-3 text-sm text-gray-700 font-medium truncate">
            {value || <span className="text-gray-400 italic">Not Available</span>}
        </div>
    </div>
);

const FormInput = ({ label, value, onChange, name, placeholder, required = false, type = "text", maxLength, onBlur, disabled = false, className = "" }: any) => (
    <div className={`w-full ${className}`}>
        <label className="block text-[11px] font-bold text-gray-700 mb-1 uppercase tracking-wide">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
            className={`w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:ring-1 focus:ring-[#00635e] focus:border-[#00635e] outline-none transition-all shadow-sm ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}`}
        />
    </div>
);

const BankDetailsPage: React.FC = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    
    // Toast State
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Data States
    const [businessData, setBusinessData] = useState<any>({});
    const [sellerData, setSellerData] = useState<any>({});
    const [bankData, setBankData] = useState({
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        bankName: '',
        branchName: ''
    });

    const getToken = () => localStorage.getItem("token");

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // --- FETCH DATA ---
    useEffect(() => {
        if (user?.id) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        const headers = { 'Authorization': `Bearer ${getToken()}` };
        try {
            // 1. Fetch Business Profile
            const busRes = await fetch(`/springboot/api/seller/profile/${user?.id}/business`, { headers });
            if (busRes.ok) setBusinessData(await busRes.json());

            // 2. Fetch Owner Basic Details
            const sellRes = await fetch(`/springboot/api/seller/profile/${user?.id}`, { headers });
            if (sellRes.ok) setSellerData(await sellRes.json());

            // 3. Fetch Existing Bank Details
            const bankRes = await fetch(`/springboot/api/seller/profile/${user?.id}/bank`, { headers });
            if (bankRes.ok) {
                const data = await bankRes.json();
                if (data && data.accountNumber) setBankData(data);
            }
        } catch (e) { console.error(e); }
        finally { setPageLoading(false); }
    };

    // --- HANDLERS ---
    const handleBankChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { name, value } = e.target;
        if (name === 'ifscCode') value = value.toUpperCase();
        setBankData({ ...bankData, [name]: value });
    };

    // Auto-Fetch Bank Name from IFSC
    const handleIfscBlur = async () => {
        if (bankData.ifscCode.length === 11) {
            try {
                const res = await fetch(`https://ifsc.razorpay.com/${bankData.ifscCode}`);
                if (!res.ok) throw new Error("Invalid Code");
                const data = await res.json();
                if (data.BANK) {
                    setBankData(prev => ({ ...prev, bankName: data.BANK, branchName: data.BRANCH }));
                    showToast(`Verified: ${data.BANK}, ${data.BRANCH}`);
                }
            } catch (e) { showToast("Invalid IFSC Code", "error"); }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/bank`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'Authorization': `Bearer ${getToken()}` 
                },
                body: JSON.stringify(bankData)
            });
            if (res.ok) showToast("Bank Details Saved Successfully!");
            else showToast("Failed to save bank details.", "error");
        } catch (e) { showToast("Server Error. Try again later.", "error"); }
        finally { setLoading(false); }
    };

    if (pageLoading) return <div className="p-10 text-center text-gray-500">Loading Banking Info...</div>;

    return (
        <div className="p-4 bg-gray-50/50 min-h-screen font-sans relative">
            
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* HEADER */}
            <div className="max-w-6xl mx-auto mb-6">
                <h1 className="text-lg md:text-xl font-black text-[#00635e] border-b border-gray-200 pb-3 flex items-center">
                    <Landmark className="mr-2 text-red-600" size={24} />
                    Bank & Payout Settings
                    <span className="ml-2 text-xs font-medium text-gray-400 font-sans hidden sm:inline-block">(Settlements & Invoices)</span>
                </h1>
            </div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 pb-20">
                
                {/* --- 1. COMPANY DETAILS (Read Only) --- */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4 text-[#00635e] border-b border-gray-100 pb-2">
                        <Building2 size={18} className="mr-2" />
                        <h2 className="text-sm font-bold uppercase tracking-wide">Company Details</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ReadOnlyField label="Company Name" value={businessData.companyName} className="md:col-span-2" />
                        <ReadOnlyField label="GST Number" value={businessData.gstNumber} />
                        <ReadOnlyField label="Office Contact" value={businessData.officeContact} />
                        <ReadOnlyField label="Registered Address" value={businessData.city ? `${businessData.city}, ${businessData.state}` : ''} className="md:col-span-2" />
                    </div>
                </div>

                {/* --- 2. OWNER DETAILS (Read Only) --- */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4 text-[#00635e] border-b border-gray-100 pb-2">
                        <User size={18} className="mr-2" />
                        <h2 className="text-sm font-bold uppercase tracking-wide">Owner Details</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ReadOnlyField label="Owner Name" value={sellerData.username} />
                        <ReadOnlyField label="Mobile" value={sellerData.mobile} />
                        <ReadOnlyField label="PAN Number" value={businessData.panNumber} />
                        <ReadOnlyField label="Aadhaar Number" value={businessData.aadhaarNumber} />
                        <ReadOnlyField label="Email" value={sellerData.email} className="md:col-span-2" />
                    </div>
                </div>

                {/* --- 3. BANK DETAILS FORM (Editable - Full Width) --- */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-50 to-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center text-[#00635e]">
                            <CreditCard className="w-5 h-5 mr-2" />
                            <h2 className="text-base font-bold">Bank Account Details</h2>
                        </div>
                        <span className="flex items-center text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">
                            <ShieldCheck size={12} className="mr-1" /> Secure
                        </span>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <FormInput 
                                label="Account Holder Name" 
                                name="accountHolderName" 
                                value={bankData.accountHolderName} 
                                onChange={handleBankChange} 
                                placeholder="Name as per Passbook" 
                                required 
                            />
                        </div>
                        
                        <FormInput 
                            label="Account Number" 
                            name="accountNumber" 
                            value={bankData.accountNumber} 
                            onChange={handleBankChange} 
                            placeholder="Enter Account Number" 
                            required 
                        />

                        <div>
                            <FormInput 
                                label="IFSC Code" 
                                name="ifscCode" 
                                value={bankData.ifscCode} 
                                onChange={handleBankChange} 
                                onBlur={handleIfscBlur}
                                placeholder="e.g. SBIN0001234" 
                                maxLength={11}
                                required 
                            />
                            <p className="text-[10px] text-gray-400 mt-1 italic ml-0.5">Auto-detects Bank & Branch on blur</p>
                        </div>

                        <FormInput 
                            label="Bank Name" 
                            name="bankName" 
                            value={bankData.bankName} 
                            onChange={handleBankChange} 
                            placeholder="Auto-filled" 
                            disabled
                        />

                        <FormInput 
                            label="Branch Name" 
                            name="branchName" 
                            value={bankData.branchName} 
                            onChange={handleBankChange} 
                            placeholder="Auto-filled" 
                            disabled
                        />

                        <div className="md:col-span-2 flex justify-end mt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center justify-center px-8 py-2.5 bg-red-600 text-white text-sm font-bold rounded-lg shadow-md hover:bg-red-700 transition active:scale-95 disabled:opacity-70"
                            >
                                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                                {loading ? 'Saving...' : 'Save Bank Details'}
                            </button>
                        </div>
                    </form>
                </div>

            </div>
        </div>
    );
};

export default BankDetailsPage;